import React from 'react';
import { Badge, Box, Button, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';
import { auth } from '../firebase';
import { getApiUrl } from '../utils/apiConfig';
import { formatNumber } from '../i18n';
import { KpiCard, KpiGrid, PageContainer, PageHeader, SectionCard, TabShell } from '../components/ui';
import { useUrlTab } from '../hooks/useUrlTab';
import { AdminBundleRulesTab } from '../components/admin/bundleRules/AdminBundleRulesTab';

interface RebateOverview {
  programs: { total: number; active: number };
  bundleRules: { total: number; active: number; needsReview: number };
  mapRowsWithInstantRebate: number;
}

const TAB_KEYS = ['overview', 'bundle-rules'] as const;
type RebateTab = (typeof TAB_KEYS)[number];

/**
 * Where rebate data lives. Rebate programs are created by stores and vendors
 * (flag `rebate_programs`) and the sheets Motzu ingests carry their rebates on
 * the MAP pricing rows, so this page reports what exists and links to it
 * rather than offering create/import controls that do not exist. The Bundle
 * rules tab (MOT-387) is where an admin reviews and edits the sold-with rules.
 */
export const AdminRebateManagementPage: React.FC = () => {
  const [tab, setTab] = useUrlTab<RebateTab>(TAB_KEYS, 'overview');
  const { data, isLoading } = useQuery<RebateOverview>({
    queryKey: ['admin-rebate-overview'],
    queryFn: async () => {
      const token = await auth.currentUser?.getIdToken();
      const response = await fetch(`${getApiUrl()}/admin/rebate-overview`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to load rebate overview');
      return (await response.json()).data;
    },
  });

  return (
    <PageContainer>
      <PageHeader
        title="Rebate programs"
        description="Where rebate data lives on the platform. Rebates ingested from vendor sheets sit on MAP pricing rows; rebate programs are created by stores and vendors."
      />

      <TabShell<RebateTab>
        aria-label="Rebate sections"
        value={tab}
        onChange={setTab}
        tabs={[
          { key: 'overview', label: 'Overview' },
          {
            key: 'bundle-rules',
            label: (
              <Badge
                color="warning"
                badgeContent={data?.bundleRules.needsReview || 0}
                invisible={!data?.bundleRules.needsReview}
                sx={{ '& .MuiBadge-badge': { right: -14 } }}
              >
                Bundle rules
              </Badge>
            ),
          },
        ]}
      >
        {tab === 'bundle-rules' ? (
          <AdminBundleRulesTab />
        ) : (
          <>
          <Box sx={{ mb: 2 }}>
            <KpiGrid minWidth={200}>
              <KpiCard
                label="MAP rows with a rebate"
                loading={isLoading}
                value={formatNumber(data?.mapRowsWithInstantRebate ?? 0)}
                note="Instant rebates ingested from vendor sheets"
              />
              <KpiCard
                label="Bundle rebate rules"
                loading={isLoading}
                value={formatNumber(data?.bundleRules.total ?? 0)}
                note={`${formatNumber(data?.bundleRules.active ?? 0)} active · ${formatNumber(data?.bundleRules.needsReview ?? 0)} need review`}
                tone={(data?.bundleRules.needsReview ?? 0) > 0 ? 'caution' : 'none'}
                colorValue={false}
              />
              <KpiCard
                label="Rebate programs"
                loading={isLoading}
                value={formatNumber(data?.programs.total ?? 0)}
                note={`${formatNumber(data?.programs.active ?? 0)} active`}
              />
            </KpiGrid>
          </Box>

          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(auto-fit, minmax(300px, 1fr))' } }}>
            <SectionCard
              title="Rebates on MAP pricing"
              subtitle="Instant rebates and bundle rebate tags come in with each vendor pricing sheet."
              actions={
                <Button size="small" component={RouterLink} to="/app/admin/map-pricing">
                  Open MAP pricing
                </Button>
              }
            >
              <Typography variant="body2" color="text.secondary">
                Filter the MAP pricing table by Gap report, Bundle rebates, to see the conditional sold-with offers.
                Upload new sheets on Pricing ingestion.
              </Typography>
            </SectionCard>

            <SectionCard
              title="Bundle rebate rules"
              subtitle="Sold-with offers become rules that are derived from sheet rows and reviewed by an admin."
            >
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Rules the sheet wording could not turn into a qualifier wait for review.
              </Typography>
              <Button size="small" onClick={() => setTab('bundle-rules')}>Review bundle rules</Button>
            </SectionCard>

            <SectionCard
              title="Store and vendor programs"
              subtitle="A program is one brand's offer over a date window, created when a store approves a sheet or a vendor sends one."
            >
              <Typography variant="body2" color="text.secondary">
                The count above is zero until the rebate programs feature is switched on for a pilot organization.
                Programs are managed by the store or vendor that owns them.
              </Typography>
            </SectionCard>
          </Box>
          </>
        )}
      </TabShell>
    </PageContainer>
  );
};

export default AdminRebateManagementPage;
